import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Public from './app/common/components/auth/Public';
import PrivateRoute from './app/common/components/auth/Private';
import ProtectedRoutes, {
  Loader,
} from './app/common/components/auth/Protected';
import { setCampaigns } from './features/dashboard/dashboardSlice';
import { setIsTourCloseModalOpen } from './features/auth/authSlice';
import CacheBuster from 'react-cache-buster';
import packageJson from '../package.json';
import { getPendingRequests } from './features/chat/chatAPI';
import { setDispositionShortCuts } from './features/chat/chatSlice';

import { TourProvider } from '@reactour/tour';
import {
  disableBody,
  enableBody,
  TOUR_DEFAULT_STYLE,
} from './app/common/tour-guide/Steps';

import './App.scss';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

const VerifyToken = lazy(() => import('./features/auth/register/VerifyToken'));
const Register = lazy(() => import('./features/auth/register/Register'));
const VerifyEmail = lazy(() => import('./features/auth/register/VerifyEmail'));
const GetStarted = lazy(() => import('./features/auth/register/GetStarted'));
const Checkout = lazy(() => import('./features/auth/register/Checkout'));
const Login = lazy(() => import('./features/auth/login/Login'));
const ForgetPassword = lazy(() =>
  import('./features/auth/forget-password/ForgetPassword')
);
const ResetPassword = lazy(() =>
  import('./features/auth/reset-password/ResetPassword')
);
// const PaymentCheckout = lazy(() =>
//   import('./features/auth/register/PaymentCheckout/PaymentCheckout')
// );

const Plans = lazy(() => import('./features/payment/plans/Plans'));
const PaymentSuccess = lazy(() =>
  import('./features/auth/register/PaymentSuccess')
);
const PaymentError = lazy(() =>
  import('./features/auth/register/PaymentError')
);
export default function App() {
  const dispatch = useDispatch();
  const { isAuthenticated, user, showTourNavigation } = useSelector(
    (state) => state.auth
  );

  // handle tour close button click,
  // confirm from user if they really want to exit tour.
  const handleTourClickClose = () => dispatch(setIsTourCloseModalOpen(true));

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(setCampaigns(user.Campaigns));
      dispatch({ type: 'SETUP_SIGNALR' });
      if (user.Role == 'Agent') {
        dispatch(getPendingRequests(user.Campaigns.map((x) => x.id)));
        dispatch(setDispositionShortCuts({ userId: user.Id, dispositions: user.PrimaryCampaign.dispositions, chatShortKeys: user.PrimaryCampaign.chatShortKeys }));
      }
    } else {
      dispatch({ type: 'LOGOUT' });
    }
  }, [isAuthenticated]);

  return (
    <CacheBuster
      currentVersion={packageJson.version}
      isEnabled={true} //If false, the library is disabled.
      isVerboseMode={false} //If true, the library writes verbose logs to console.
      loadingComponent={<Loader />} //If not pass, nothing appears at the time of new version check.
    >
      <TourProvider
        showBadge={false}
        showNavigation={showTourNavigation}
        showDots={false}
        disableFocusLock={true}
        disableDotsNavigation={true}
        disableKeyboardNavigation={true}
        disableInteraction={user.Role === 'Agent'}
        scrollSmooth={true}
        onClickMask={({ setIsOpen }) => setIsOpen(true)}
        onClickClose={handleTourClickClose}
        afterOpen={disableBody}
        beforeClose={enableBody}
        styles={TOUR_DEFAULT_STYLE}
        padding={{ mask: 4 }}
      >
        <Router>
          <Suspense fallback={<Loader />}>
            <Switch>
              <Public isAuthenticated={isAuthenticated} path='/login'>
                <Login />
              </Public>
              <Public isAuthenticated={isAuthenticated} path='/forget-password'>
                <ForgetPassword />
              </Public>
              <Public
                isAuthenticated={isAuthenticated}
                path='/auth/password/reset/:token/:email'
              >
                <ResetPassword />
              </Public>
              <Public
                isAuthenticated={isAuthenticated}
                path='/auth/password/set/:token/:email'
              >
                <ResetPassword />
              </Public>
              <Public
                isAuthenticated={isAuthenticated}
                path='/auth/register/:id'
              >
                <Register />
              </Public>
              <Public
                isAuthenticated={isAuthenticated}
                path='/auth/verify-email'
              >
                <VerifyEmail />
              </Public>
              <Public
                isAuthenticated={isAuthenticated}
                path='/auth/get-started'
              >
                <GetStarted />
              </Public>
              <Public
                isAuthenticated={isAuthenticated}
                path='/payment/checkout'
              >
                <Checkout />
              </Public>
              <Public
                isAuthenticated={isAuthenticated}
                path='/auth/verify/:token/:accountId/:productId'
              >
                <VerifyToken />
              </Public>
              <Route path='/payment/success' component={PaymentSuccess} />
              <Route path='/payment/error' component={PaymentError} />
              <Route path='/get-started' component={GetStarted} />
              {/* <Public
                isAuthenticated={isAuthenticated}
                path='/payment/success'
              >
                <PaymentSuccess />
              </Public>
              <Public
                isAuthenticated={isAuthenticated}
                path='/payment/error'
              >
                <PaymentError />
              </Public> */}
              {/* case for payment failed (Account Supervisor Case) */}
              {user.account?.accountError ? (
                <PrivateRoute path='/' isAuthenticated={isAuthenticated}>
                  <Plans isNavigateOnCheckout={true} />
                </PrivateRoute>
              ) : (
                <PrivateRoute path='/' isAuthenticated={isAuthenticated}>
                  <ProtectedRoutes
                    role={user.Role}
                    showVoc={user.isVocEnabled}
                  />
                </PrivateRoute>
              )}
            </Switch>
          </Suspense>
        </Router>
      </TourProvider>
    </CacheBuster>
  );
}
